import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import type CK from '~/types/contentking';

import AttachedIcon from '~/components/patterns/structuredValues/AttachedIcon';
import BasicIcon, {
	BasicIconType,
} from '~/components/patterns/icons/BasicIcon';
import BlankValue from '~/components/patterns/values/BlankValue';
import Copy from '~/components/logic/Copy';
import HeaderLabel from '~/components/atoms/dataTables/HeaderLabel';
import Hint from '~/components/patterns/hints/hint/Hint';
import RealtimeValueUpdateHighlight from '~/components/patterns/values/RealtimeValueUpdateHighlight';
import SearchEngineLastVisitFormatter from '~/components/logic/formatters/SearchEngineLastVisitFormatter';
import SearchEngineVisitFrequencyFormatter from '~/components/logic/formatters/SearchEngineVisitFrequencyFormatter';
import SimpleTable from '~/components/patterns/tables/simpleTable/SimpleTable';
import SimpleTableCell, {
	SimpleTableCellAlignment,
	SimpleTableCellType,
} from '~/components/patterns/tables/simpleTable/SimpleTableCell';
import SquareSkeleton from '~/components/patterns/loaders/SquareSkeleton';



const messages = defineMessages({
	bingDesktop: {
		id: 'ui.searchEngineActivity.bingDesktop',
	},
	bingMobile: {
		id: 'ui.searchEngineActivity.bingMobile',
	},
	frequency: {
		id: 'ui.searchEngineActivity.frequency',
	},
	googleDesktop: {
		id: 'ui.searchEngineActivity.googleDesktop',
	},
	googleMobile: {
		id: 'ui.searchEngineActivity.googleMobile',
	},
	openaiBot: {
		id: 'ui.searchEngineActivity.openaiBot',
	},
	openaiSearch: {
		id: 'ui.searchEngineActivity.openaiSearch',
	},
	openaiUser: {
		id: 'ui.searchEngineActivity.openaiUser',
	},
	perplexityBot: {
		id: 'ui.searchEngineActivity.perplexityBot',
	},
	perplexityUser: {
		id: 'ui.searchEngineActivity.perplexityUser',
	},
	lastVisited: {
		id: 'ui.searchEngineActivity.lastVisited',
	},
	noData: {
		id: 'ui.values.noData',
	},
	searchEngine: {
		id: 'ui.searchEngineActivity.searchEngine',
	},
});



const COLUMN_WIDTHS = {
	FREQUENCY: 140,
	LAST_VISIT: 130,
} as const;



type LastVisit = CK.Timestamp | null;
type VisitFrequency = number | null;

type Props = {
	hasCompactLabels?: boolean,
	lastVisitOfBingDesktop: LastVisit,
	lastVisitOfBingMobile: LastVisit,
	lastVisitOfGoogleDesktop: LastVisit,
	lastVisitOfGoogleMobile: LastVisit,
	lastVisitOfOpenAIBot: LastVisit,
	lastVisitOfOpenAISearch: LastVisit,
	lastVisitOfOpenAIUser: LastVisit,
	lastVisitOfPerplexityBot: LastVisit,
	lastVisitOfPerplexityUser: LastVisit,
	loading?: boolean,
	visitFrequencyOfBingDesktop: VisitFrequency,
	visitFrequencyOfBingMobile: VisitFrequency,
	visitFrequencyOfGoogleDesktop: VisitFrequency,
	visitFrequencyOfGoogleMobile: VisitFrequency,
	visitFrequencyOfOpenAIBot: VisitFrequency,
	visitFrequencyOfOpenAISearch: VisitFrequency,
	visitFrequencyOfOpenAIUser: VisitFrequency,
	visitFrequencyOfPerplexityBot: VisitFrequency,
	visitFrequencyOfPerplexityUser: VisitFrequency,
};

const SearchEngineActivityTable: React.FC<Props> = (props) => {
	const {
		hasCompactLabels = false,
		lastVisitOfBingDesktop,
		lastVisitOfBingMobile,
		lastVisitOfGoogleDesktop,
		lastVisitOfGoogleMobile,
		lastVisitOfOpenAIBot,
		lastVisitOfOpenAISearch,
		lastVisitOfOpenAIUser,
		lastVisitOfPerplexityBot,
		lastVisitOfPerplexityUser,
		loading,
		visitFrequencyOfBingDesktop,
		visitFrequencyOfBingMobile,
		visitFrequencyOfGoogleDesktop,
		visitFrequencyOfGoogleMobile,
		visitFrequencyOfOpenAIBot,
		visitFrequencyOfOpenAISearch,
		visitFrequencyOfOpenAIUser,
		visitFrequencyOfPerplexityBot,
		visitFrequencyOfPerplexityUser,
	} = props;

	const searchEngines = [
		{
			title: <Copy {...messages.openaiBot} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.OpenAI} />
					<BasicIcon type={BasicIconType.Bot} />
				</>
			),
			lastVisit: lastVisitOfOpenAIBot,
			visitFrequency: visitFrequencyOfOpenAIBot,
		},
		{
			title: <Copy {...messages.openaiSearch} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.OpenAI} />
					<BasicIcon type={BasicIconType.Search} />
				</>
			),
			lastVisit: lastVisitOfOpenAISearch,
			visitFrequency: visitFrequencyOfOpenAISearch,
		},
		{
			title: <Copy {...messages.openaiUser} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.OpenAI} />
					<BasicIcon type={BasicIconType.User} />
				</>
			),
			lastVisit: lastVisitOfOpenAIUser,
			visitFrequency: visitFrequencyOfOpenAIUser,
		},
		{
			title: <Copy {...messages.perplexityBot} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.Perplexity} />
					<BasicIcon type={BasicIconType.Bot} />
				</>
			),
			lastVisit: lastVisitOfPerplexityBot,
			visitFrequency: visitFrequencyOfPerplexityBot,
		},
		{
			title: <Copy {...messages.perplexityUser} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.Perplexity} />
					<BasicIcon type={BasicIconType.User} />
				</>
			),
			lastVisit: lastVisitOfPerplexityUser,
			visitFrequency: visitFrequencyOfPerplexityUser,
		},
		{
			title: <Copy {...messages.googleDesktop} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.Google} />
					<BasicIcon type={BasicIconType.Desktop} />
				</>
			),
			lastVisit: lastVisitOfGoogleDesktop,
			visitFrequency: visitFrequencyOfGoogleDesktop,
		},
		{
			title: <Copy {...messages.googleMobile} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.Google} />
					<BasicIcon type={BasicIconType.Mobile} />
				</>
			),
			lastVisit: lastVisitOfGoogleMobile,
			visitFrequency: visitFrequencyOfGoogleMobile,
		},
		{
			title: <Copy {...messages.bingDesktop} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.Bing} />
					<BasicIcon type={BasicIconType.Desktop} />
				</>
			),
			lastVisit: lastVisitOfBingDesktop,
			visitFrequency: visitFrequencyOfBingDesktop,
		},
		{
			title: <Copy {...messages.bingMobile} />,
			icon: (
				<>
					<BasicIcon type={BasicIconType.Bing} />
					<BasicIcon type={BasicIconType.Mobile} />
				</>
			),
			lastVisit: lastVisitOfBingMobile,
			visitFrequency: visitFrequencyOfBingMobile,
		},
	];


	return (
		<SimpleTable
			className="search-engine-activity-table"
			header={[
				<SimpleTableCell
					key="searchEngine"
					type={SimpleTableCellType.Header}
				>
					{!hasCompactLabels && (
						<HeaderLabel>
							<FormattedMessage {...messages.searchEngine} />
						</HeaderLabel>
					)}
				</SimpleTableCell>,
				<SimpleTableCell
					alignment={SimpleTableCellAlignment.Right}
					key="frequency"
					type={SimpleTableCellType.Header}
					width={COLUMN_WIDTHS.FREQUENCY}
				>
					<HeaderLabel>
						<FormattedMessage {...messages.frequency} />
					</HeaderLabel>
				</SimpleTableCell>,
				<SimpleTableCell
					key="last-visited"
					type={SimpleTableCellType.Header}
					width={COLUMN_WIDTHS.LAST_VISIT}
				>
					<HeaderLabel>
						<FormattedMessage {...messages.lastVisited} />
					</HeaderLabel>
				</SimpleTableCell>,
			]}
			rows={
				searchEngines.map((searchEngine, index) => {
					return [
						[
							<SimpleTableCell
								key={`title-${index}`}
								strongBorder={index === 2 || index === 4}
							>
								{hasCompactLabels ? (
									<Hint
										popup={searchEngine.title}
									>
										{searchEngine.icon}
									</Hint>
								) : (
									<AttachedIcon icon={searchEngine.icon}>
										{searchEngine.title}
									</AttachedIcon>
								)}
							</SimpleTableCell>,
							<SimpleTableCell
								alignment={SimpleTableCellAlignment.Right}
								key={`visit-frequency-${index}`}
								strongBorder={index === 2 || index === 4}
								width={COLUMN_WIDTHS.FREQUENCY}
							>
								<SearchEngineVisitFrequency
									frequency={searchEngine.visitFrequency}
									loading={loading}
								/>
							</SimpleTableCell>,
							<SimpleTableCell
								key={`last-visited-${index}`}
								strongBorder={index === 2 || index === 4}
								width={COLUMN_WIDTHS.LAST_VISIT}
							>
								<SearchEngineLastVisit
									lastVisit={searchEngine.lastVisit}
									loading={loading}
								/>
							</SimpleTableCell>,
						],
					];
				})
			}
		/>
	);
};



type SearchEngineLastVisitProps = {
	lastVisit: LastVisit,
	loading?: boolean,
};

const SearchEngineLastVisit: React.FC<SearchEngineLastVisitProps> = (props) => {
	const {
		lastVisit,
		loading,
	} = props;

	return (
		<RealtimeValueUpdateHighlight
			disabled={loading}
			value={lastVisit}
		>
			{loading ? (
				<SquareSkeleton width={100} />
			) : lastVisit !== null ? (
				<SearchEngineLastVisitFormatter value={lastVisit} />
			) : (
				<BlankValue>
					<FormattedMessage {...messages.noData} />
				</BlankValue>
			)}
		</RealtimeValueUpdateHighlight>
	);
}



type SearchEngineVisitFrequencyProps = {
	frequency: VisitFrequency,
	loading?: boolean,
};

const SearchEngineVisitFrequency: React.FC<SearchEngineVisitFrequencyProps> = (props) => {
	const {
		frequency,
		loading,
	} = props;

	return (
		<RealtimeValueUpdateHighlight
			disabled={loading}
			value={frequency}
		>
			{loading ? (
				<SquareSkeleton width={50} />
			) : frequency !== null ? (
				<SearchEngineVisitFrequencyFormatter
					unit="per month"
					value={frequency}
				/>
			) : (
				<BlankValue>
					<FormattedMessage {...messages.noData} />
				</BlankValue>
			)}
		</RealtimeValueUpdateHighlight>
	);
}


export default SearchEngineActivityTable;
